import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fullSync } from "../lib/sync";
import { endpoints } from "../lib/api";
import { FRANCHISE_SHORT, APP_NAME } from "../lib/branding";
import { useRomFooter } from "../context/RomFooterContext";
import {
    Gauge,
    Calculator,
    Users,
    ListChecks,
    FileText,
    Brain,
    Wrench,
    Gear,
    SignOut,
    WifiHigh,
    WifiSlash,
    CloudArrowUp,
    CircleNotch,
    List as Hamburger,
    CaretLeft,
    CaretDown,
    Stack,
    Clock,
    CheckSquare,
    UsersThree,
    ClockCounterClockwise,
    PencilSimpleLine,
    Kanban,
    CalendarBlank,
    ChartPieSlice,
    Truck,
    ClipboardText,
    Handshake,
    Scroll,
    ChartLineUp,
    ArrowLeft,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
    {
        key: "crm",
        label: "CRM",
        icon: Handshake,
        items: [
            { to: "/crm/dashboard", label: "Dashboard", icon: ChartLineUp },
            { to: "/crm", label: "Pipeline", icon: Handshake, end: true },
            { to: "/clients", label: "Accounts", icon: Users },
            { to: "/contracts", label: "Contracts", icon: Scroll },
        ],
    },
    {
        key: "roms",
        label: "ROMs",
        icon: Stack,
        items: [
            { to: "/roms-overview", label: "Overview", icon: Gauge },
            { to: "/estimates", label: "Estimates", icon: Calculator, badge: "Live" },
            { to: "/rate-schedule", label: "Rate Schedule", icon: ListChecks },
            { to: "/documents", label: "Documents", icon: FileText },
            { to: "/calibration", label: "Coefficient Tuner", icon: Brain },
            { to: "/data-gaps", label: "Data Gaps", icon: Wrench },
        ],
    },
    {
        key: "projects",
        label: "Projects",
        icon: Kanban,
        items: [
            { to: "/projects", label: "Pipeline", icon: Kanban },
            { to: "/schedules", label: "Calendar", icon: CalendarBlank },
            { to: "/field/checklists", label: "Checklist Templates", icon: ClipboardText },
        ],
    },
    {
        key: "timesheets",
        label: "Timesheets",
        icon: Clock,
        items: [
            { to: "/timesheets/capture", label: "Capture Time", icon: PencilSimpleLine },
            { to: "/timesheets/approvals", label: "Approvals", icon: CheckSquare },
            { to: "/timesheets/employees", label: "Employees", icon: UsersThree },
            { to: "/timesheets/subcontractors", label: "Subcontractors", icon: Truck },
            { to: "/timesheets/site-register", label: "Site Register", icon: ClipboardText },
            { to: "/timesheets/entries", label: "Time Entries", icon: ClockCounterClockwise },
        ],
    },
];

const TOP_LEVEL = [{ to: "/dashboard", label: "Command Center", icon: ChartPieSlice }];
const STANDALONE = [{ to: "/settings", label: "Settings", icon: Gear }];

const LS_COLLAPSED_KEY = "rom_sidebar_collapsed";
const LS_GROUPS_KEY = "rom_sidebar_groups";

export default function Layout({ children }) {
    const { user, online, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const onCommandCenter = location.pathname === "/dashboard";
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_COLLAPSED_KEY) === "1");
    const [openGroups, setOpenGroups] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(LS_GROUPS_KEY) || "null");
            if (saved) return saved;
        } catch {}
        return { roms: true, timesheets: true, projects: true, crm: true };
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [lastSyncMsg, setLastSyncMsg] = useState("");
    const [proposalAlerts, setProposalAlerts] = useState(0);
    const [crmAlerts, setCrmAlerts] = useState(0);
    const { footer } = useRomFooter();

    useEffect(() => {
        localStorage.setItem(LS_COLLAPSED_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    /* Poll for shared-proposal alerts (accepted / changes requested) so the
     * Documents nav shows a live badge. Also refreshes when the Documents page
     * marks them seen (custom 'proposals-seen' event). */
    useEffect(() => {
        if (!user) return;
        let alive = true;
        const fetchAlerts = async () => {
            try {
                const { data } = await endpoints.proposalAlerts();
                if (alive) setProposalAlerts(data?.count || 0);
            } catch { /* offline / optional */ }
            try {
                const { data } = await endpoints.crmAlerts();
                if (alive) setCrmAlerts(data?.count || 0);
            } catch { /* offline / optional */ }
        };
        fetchAlerts();
        const id = setInterval(fetchAlerts, 45000);
        const onSeen = () => fetchAlerts();
        window.addEventListener("proposals-seen", onSeen);
        return () => { alive = false; clearInterval(id); window.removeEventListener("proposals-seen", onSeen); };
    }, [user]);

    useEffect(() => {
        localStorage.setItem(LS_GROUPS_KEY, JSON.stringify(openGroups));
    }, [openGroups]);

    function toggleGroup(key) {
        setOpenGroups((g) => ({ ...g, [key]: !g[key] }));
    }

    useEffect(() => {
        if (!online || !user) return;
        runSync(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [online, user]);

    async function runSync(silent = false) {
        if (!online) {
            if (!silent) setLastSyncMsg("Offline — changes queued locally");
            return;
        }
        setSyncing(true);
        const r = await fullSync();
        setSyncing(false);
        setLastSyncMsg(r.pushed?.ok && r.pulled?.ok
            ? `Synced · ${r.pushed.flushed || 0} pushed`
            : "Sync failed — will retry");
        setTimeout(() => setLastSyncMsg(""), 4000);
    }

    const roleColor = {
        admin: "chip-orange",
        manager: "chip-navy",
        estimator: "chip-green",
    }[user?.role] || "chip-gray";

    const sidebarWidth = collapsed ? "w-[68px]" : "w-[244px]";

    return (
        <div className="min-h-screen flex" data-testid="app-shell">
            {/* MOBILE OVERLAY */}
            {mobileOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-30 bg-black/50"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            {/* SIDEBAR */}
            <aside
                data-testid="sidebar"
                className={`no-print fixed lg:sticky top-0 left-0 z-40 h-screen lg:h-screen
                    bg-[var(--rom-navy)] text-white border-r border-black/30
                    flex flex-col transition-[width,transform] duration-200 ease-out
                    ${sidebarWidth}
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
            >
                {/* Brand */}
                <div className={`flex items-center gap-3 px-4 py-4 border-b border-white/10 ${collapsed ? "justify-center" : ""}`}>
                    <button
                        onClick={() => navigate("/dashboard")}
                        className="flex items-center gap-3 min-w-0"
                        data-testid="brand-link"
                    >
                        <img
                            src="/servpro-logo.png"
                            alt="SERVPRO"
                            className="w-10 h-10 object-contain shrink-0"
                            draggable={false}
                        />
                        {!collapsed && (
                            <div className="min-w-0">
                                <div className="font-display font-black text-base leading-none">
                                    {APP_NAME}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/80 mt-1 truncate">
                                    {FRANCHISE_SHORT}
                                </div>
                            </div>
                        )}
                    </button>
                </div>

                {/* Collapse toggle (desktop) */}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    data-testid="sidebar-toggle"
                    title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                    className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-[var(--rom-orange)] text-[var(--rom-navy)] items-center justify-center shadow-md hover:scale-110 transition-transform z-10"
                >
                    {collapsed ? <Hamburger size={13} weight="bold" /> : <CaretLeft size={13} weight="bold" />}
                </button>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
                    <div className="space-y-0.5 pb-1 mb-1 border-b border-white/10">
                        {TOP_LEVEL.map((item) => (
                            <NavItem key={item.to} item={item} collapsed={collapsed} onNav={() => setMobileOpen(false)} />
                        ))}
                    </div>
                    {NAV_GROUPS.map((group) => {
                        const open = collapsed ? true : openGroups[group.key] !== false;
                        return (
                            <div key={group.key} className="space-y-0.5">
                                {!collapsed ? (
                                    <button
                                        onClick={() => toggleGroup(group.key)}
                                        data-testid={`nav-group-${group.key}`}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/55 hover:text-white/90 transition-colors"
                                    >
                                        <group.icon size={14} weight="bold" className="shrink-0 text-white/70" />
                                        <span className="flex-1 text-left">{group.label}</span>
                                        <CaretDown
                                            size={11}
                                            weight="bold"
                                            className={`transition-transform ${open ? "" : "-rotate-90"}`}
                                        />
                                    </button>
                                ) : (
                                    <div className="flex justify-center py-1.5" title={group.label}>
                                        <group.icon size={15} weight="bold" className="text-white/40" />
                                    </div>
                                )}
                                {open && (
                                    <div className={collapsed ? "space-y-0.5" : "space-y-0.5 pl-1"}>
                                        {group.items.map((item) => (
                                            <NavItem key={item.to} item={item} collapsed={collapsed} alertCount={item.to === "/documents" ? proposalAlerts : item.to === "/crm/dashboard" ? crmAlerts : 0} onNav={() => setMobileOpen(false)} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    <div className="pt-2 mt-2 border-t border-white/10 space-y-0.5">
                        {STANDALONE.map((item) => (
                            <NavItem key={item.to} item={item} collapsed={collapsed} onNav={() => setMobileOpen(false)} />
                        ))}
                    </div>
                </nav>

                {/* Footer user */}
                <div className="border-t border-white/10 p-3 space-y-2">
                    {!collapsed ? (
                        <>
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-full bg-[var(--rom-navy-600)] flex items-center justify-center font-display font-bold text-sm shrink-0">
                                    {(user?.name || "?").charAt(0)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold truncate">{user?.name}</div>
                                    <div className="text-[11px] text-white/75 truncate font-mono">{user?.email}</div>
                                </div>
                                <button
                                    onClick={logout}
                                    data-testid="logout-button"
                                    title="Log out"
                                    className="p-1.5 rounded-[4px] text-white/80 hover:text-white hover:bg-[var(--rom-navy-600)]"
                                >
                                    <SignOut size={14} weight="bold" />
                                </button>
                            </div>
                            <div className={`chip ${roleColor} w-full justify-center`} data-testid="role-badge">
                                {user?.role}
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <div
                                className="w-9 h-9 rounded-full bg-[var(--rom-navy-600)] flex items-center justify-center font-display font-bold text-sm"
                                title={`${user?.name} · ${user?.role}`}
                                data-testid="role-badge"
                            >
                                {(user?.name || "?").charAt(0)}
                            </div>
                            <button
                                onClick={logout}
                                data-testid="logout-button"
                                title="Log out"
                                className="p-1.5 rounded-[4px] text-white/80 hover:text-white hover:bg-[var(--rom-navy-600)]"
                            >
                                <SignOut size={14} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* MAIN */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top utility bar */}
                <header className="no-print sticky top-0 z-20 bg-white border-b border-[var(--rom-border)]">
                    <div className="flex items-center gap-3 px-4 lg:px-6 py-2.5">
                        <button
                            onClick={() => setMobileOpen(true)}
                            data-testid="mobile-hamburger"
                            className="lg:hidden p-2 rounded text-[var(--rom-navy)] hover:bg-slate-100"
                            title="Open menu"
                        >
                            <Hamburger size={20} weight="bold" />
                        </button>

                        {!onCommandCenter && (
                            <button
                                onClick={() => navigate("/dashboard")}
                                data-testid="back-to-command-center"
                                className="flex items-center gap-1.5 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-navy)] px-2.5 py-1.5 rounded-[4px] hover:bg-slate-100 transition-colors border border-transparent hover:border-[var(--rom-border)]"
                            >
                                <ArrowLeft size={13} weight="bold" />
                                <span>Command Center</span>
                            </button>
                        )}

                        <div className="flex-1" />

                        <button
                            onClick={() => runSync(false)}
                            disabled={syncing}
                            data-testid="sync-button"
                            className="flex items-center gap-2 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] px-2 py-1"
                            title={lastSyncMsg || "Sync to server"}
                        >
                            {syncing ? (
                                <CircleNotch size={14} className="animate-spin" />
                            ) : (
                                <CloudArrowUp size={14} weight="bold" />
                            )}
                            <span className="hidden sm:inline">Sync</span>
                        </button>

                        <div
                            className="flex items-center gap-1.5 text-xs font-semibold"
                            data-testid="online-indicator"
                        >
                            {online ? (
                                <>
                                    <WifiHigh size={14} className="text-[var(--rom-green)]" weight="bold" />
                                    <span className="text-[var(--rom-text-2)] hidden sm:inline">Online</span>
                                </>
                            ) : (
                                <>
                                    <WifiSlash size={14} className="text-amber-600" weight="bold" />
                                    <span className="text-amber-700">Offline</span>
                                </>
                            )}
                        </div>
                    </div>
                    {lastSyncMsg && (
                        <div className="bg-[var(--rom-orange-soft)] text-xs px-6 py-1 text-[var(--rom-orange-dark)] font-semibold">
                            {lastSyncMsg}
                        </div>
                    )}
                    <OfflineReadyBanner />
                </header>

                <main className="flex-1 min-w-0">{children}</main>

                <footer className="no-print py-4 text-center text-[11px] text-[var(--rom-text-3)] uppercase tracking-[0.16em]" data-testid="rom-footer-confidence">
                    {footer
                        ? `ROM is a directional budget · ±${footer.pct}% confidence${footer.calibrated ? " (calibrated)" : ""} · not a contract figure`
                        : "ROM is a directional budget · ±15% confidence · not a contract figure"}
                </footer>
            </div>
        </div>
    );
}

const OFFLINE_READY_KEY = "rom_offline_ready_dismissed";

function NavItem({ item, collapsed, onNav, alertCount = 0 }) {
    const { to, label, icon: Icon, badge, end } = item;
    return (
        <NavLink
            to={to}
            end={end}
            onClick={onNav}
            data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-[4px] text-sm font-medium transition-colors group relative ${
                    collapsed ? "justify-center" : ""
                } ${
                    isActive
                        ? "bg-[var(--rom-orange)] text-white"
                        : "text-white/85 hover:bg-[var(--rom-navy-600)] hover:text-white"
                }`
            }
        >
            <Icon size={17} weight="bold" className="shrink-0" />
            {!collapsed && (
                <>
                    <span className="truncate flex-1">{label}</span>
                    {alertCount > 0 && (
                        <span className="text-[10px] font-bold bg-[var(--rom-orange)] text-white min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full" data-testid="nav-documents-alert">
                            {alertCount}
                        </span>
                    )}
                    {badge && alertCount === 0 && (
                        <span className="text-[9px] font-bold tracking-wider uppercase bg-[var(--rom-green)] text-white px-1.5 py-0.5 rounded-sm">
                            {badge}
                        </span>
                    )}
                </>
            )}
            {collapsed && (alertCount > 0 || badge) && (
                <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${alertCount > 0 ? "bg-[var(--rom-orange)]" : "bg-[var(--rom-green)]"}`} />
            )}
        </NavLink>
    );
}

function OfflineReadyBanner() {
    const [show, setShow] = useState(false);
    useEffect(() => {
        let justSignedIn = false;
        try { justSignedIn = sessionStorage.getItem("rom_just_signed_in") === "1"; } catch {}
        let dismissed = false;
        try { dismissed = localStorage.getItem(OFFLINE_READY_KEY) === "1"; } catch {}
        if (justSignedIn && !dismissed) setShow(true);
        // NOTE: intentionally NOT removed here. The one-time service-worker
        // "first activation" reload (index.js) can fire moments after login
        // and wipe this in-memory `show` state before the user ever sees the
        // banner painted. Leaving the sessionStorage flag in place lets the
        // post-reload remount pick it up and actually show it; it's only
        // consumed once the user acknowledges it below.
    }, []);
    if (!show) return null;
    return (
        <div
            className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 px-6 py-2.5 flex items-center gap-3"
            data-testid="offline-ready-banner"
        >
            <WifiHigh size={16} weight="bold" className="text-emerald-700 shrink-0" />
            <div className="text-xs leading-relaxed flex-1">
                <b>Offline mode is now active on this device.</b> You can continue using the app without internet — your changes will sync automatically when you reconnect.
            </div>
            <button
                onClick={() => {
                    try { localStorage.setItem(OFFLINE_READY_KEY, "1"); } catch {}
                    try { sessionStorage.removeItem("rom_just_signed_in"); } catch {}
                    setShow(false);
                }}
                className="text-emerald-800 hover:text-emerald-900 font-semibold text-xs underline shrink-0"
                data-testid="offline-ready-dismiss"
            >
                Got it
            </button>
        </div>
    );
}
